import axios from 'axios';
import { notifyParrainageMutation } from './parrainageRefresh';

// CRM et Inside VNX utilisent le backend explicitement configuré dans l'environnement.
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
if (!BACKEND_URL) throw new Error('REACT_APP_BACKEND_URL doit être configuré.');
export const API_URL = `${BACKEND_URL.replace(/\/$/, '')}/api`;

const api = axios.create({
    baseURL: API_URL,
    timeout: 30000, // 30 s : absorbe les démarrages à froid / pics d'affluence (retry au-delà)
    headers: {
        'Content-Type': 'application/json',
    },
});

// Retry automatique pour les erreurs réseau transitoires
const retryRequest = async (config, retries = 2, delay = 1000) => {
    for (let i = 0; i < retries; i++) {
        try {
            await new Promise(r => setTimeout(r, delay * (i + 1)));
            const instance = axios.create({
                baseURL: config.baseURL || API_URL,
                timeout: config.timeout || 30000, // conserve le timeout d'origine (ex. 60s pilotage)
            });
            const url = config?.url || '';
            const isPublicAuth = ['/auth/login', '/auth/accountant-login', '/auth/register', '/auth/forgot-password', '/auth/reset-password'].some((p) => url.includes(p));
            if (!isPublicAuth) {
                const token = localStorage.getItem('valnexo_token');
                if (token) {
                    config.headers = { ...config.headers, Authorization: `Bearer ${token}` };
                }
            } else if (config.headers) {
                // Retry d'un login : on veille à ne pas hériter d'un header Authorization
                // ajouté par un intercepteur externe (mécanisme de failsafe).
                delete config.headers.Authorization;
                delete config.headers.authorization;
            }
            return await instance(config);
        } catch (retryErr) {
            if (i === retries - 1) throw retryErr;
        }
    }
};

// Add auth token to requests
// IMPORTANT : on N'AJOUTE PAS le header Authorization sur les routes publiques
// d'authentification. Envoyer un `Bearer <token>` invalide ou d'un autre user à
// /auth/login peut, dans certains contextes réseau/proxy, être interprété comme
// une tentative d'attaque et faire tomber la requête avant qu'elle n'atteigne
// le backend (RESET de connexion, blocage extension, etc.). En bypassant, on
// garantit que le login part toujours "propre".
const AUTH_PUBLIC_PATHS = ['/auth/login', '/auth/accountant-login', '/auth/register', '/auth/forgot-password', '/auth/reset-password'];
api.interceptors.request.use((config) => {
    const url = config?.url || '';
    const isPublicAuth = AUTH_PUBLIC_PATHS.some((p) => url.includes(p));
    if (isPublicAuth) {
        // Nettoie explicitement toute Authorization résiduelle (héritée d'une session
        // précédente ou d'un autre onglet) — le login doit être 100% anonyme.
        if (config.headers) {
            delete config.headers.Authorization;
            delete config.headers.authorization;
        }
        return config;
    }
    const token = localStorage.getItem('valnexo_token');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// Handle errors with retry for network issues
api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const config = error.config;
        
        // Cas 1: Erreur réseau (pas de response) - Retry automatique
        if (!error.response) {
            if (!config._retried && !config.skipRetry) {
                config._retried = true;
                console.warn('[API] Erreur réseau - tentative de reconnexion...');
                try {
                    return await retryRequest(config);
                } catch {
                    // Retry failed too
                }
            }
            return Promise.reject(error);
        }
        
        // Cas 2: Timeout - Retry une fois
        if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
            if (!config._retried && !config.skipRetry) {
                config._retried = true;
                console.warn('[API] Timeout - nouvelle tentative...');
                try {
                    return await retryRequest(config, 1, 500);
                } catch {
                    // Retry failed
                }
            }
            return Promise.reject(error);
        }
        
        // Cas 3: Erreur 401 (token invalide/expiré) - Déconnecter sauf sur login
        if (error.response?.status === 401) {
            const isLoginRoute = config?.url?.includes('/auth/login');
            const isAccountantLogin = config?.url?.includes('/auth/accountant-login');
            if (!isLoginRoute && !isAccountantLogin) {
                console.warn('[API] Token invalide ou expiré - déconnexion');
                localStorage.removeItem('valnexo_token');
                localStorage.removeItem('valnexo_user');
                window.location.href = '/login';
            }
        }
        
        // Cas 4: Erreur serveur 500/502/503/504 (passerelle/affluence) - Retry une fois
        if ([500, 502, 503, 504].includes(error.response?.status) && !config._retried && !config.skipRetry) {
            config._retried = true;
            console.warn(`[API] Erreur serveur ${error.response.status} - nouvelle tentative...`);
            try {
                return await retryRequest(config, 1, 1000);
            } catch {
                // Server still failing
            }
        }
        
        return Promise.reject(error);
    }
);

// Après succès uniquement, y compris après un retry. Aucun changement d'auth.
api.interceptors.response.use((response) => {
    notifyParrainageMutation(response.config);
    return response;
});

export const wallAttachmentsAPI = {
    upload: (prospectId, module, file, onUploadProgress, signal) => {
        const data = new FormData(); data.append('file', file); data.append('module', module);
        return api.post(`/prospects/${prospectId}/wall-attachments`, data, {
            headers: { 'Content-Type': 'multipart/form-data' }, onUploadProgress, signal, timeout: 180000, skipRetry: true,
        });
    },
    metadata: (prospectId, id, signal) => api.get(`/prospects/${prospectId}/wall-attachments/${id}`, { signal, skipRetry: true }),
    preview: (prospectId, id, signal, timeout = 30000) => api.get(`/prospects/${prospectId}/wall-attachments/${id}/preview`, { responseType: 'blob', signal, skipRetry: true, timeout }),
    thumbnail: (prospectId, id, signal) => api.get(`/prospects/${prospectId}/wall-attachments/${id}/thumbnail`, { responseType: 'blob', signal, skipRetry: true, timeout: 15000 }),
    download: (prospectId, id, signal) => api.get(`/prospects/${prospectId}/wall-attachments/${id}/download`, { responseType: 'blob', signal, skipRetry: true }),
    retry: (prospectId, id) => api.post(`/prospects/${prospectId}/wall-attachments/${id}/retry-preview`, {}, { skipRetry: true }),
    remove: (prospectId, id) => api.delete(`/prospects/${prospectId}/wall-attachments/${id}`, { skipRetry: true }),
};

export default api;

// Auth API
export const authAPI = {
    login: (data) => api.post('/auth/login', data, { timeout: 30000 }),
    register: (data) => api.post('/auth/register', data),
    me: () => api.get('/auth/me'),
};

// Onboarding API (parcours candidat Apprenant → Apporteur)
export const onboardingAPI = {
    register: (data) => api.post('/onboarding/register', data), // public
    requestValidation: () => api.post('/onboarding/request-validation'),
    myStatus: () => api.get('/onboarding/my-status'),
    listCandidates: (params) => api.get('/onboarding/candidates', { params }),
    validate: (id, data) => api.post(`/onboarding/candidates/${id}/validate`, data),
    reject: (id, data) => api.post(`/onboarding/candidates/${id}/reject`, data),
    assign: (id, data) => api.post(`/onboarding/candidates/${id}/assign`, data),
};

// Users API
export const usersAPI = {
    getAll: (params) => api.get('/users', { params }),
    // Endpoint ultra-léger pour selects/checkboxes (assignation, participants) — payload minimal
    getPicker: (config = {}) => api.get('/users/picker', config),
    getById: (id) => api.get(`/users/${id}`),
    update: (id, data) => api.put(`/users/${id}`, data),
    delete: (id) => api.delete(`/users/${id}`),
    getVPs: () => api.get('/users/vps/list'),
    getVPTeam: (vpId) => api.get(`/users/vp/${vpId}/team`),
    getVPTeamHierarchy: (vpId) => api.get(`/users/vp/${vpId}/team/hierarchy`),
    getApporteursForParent: (vpId) => api.get(`/users/vp/${vpId}/apporteurs-for-parent`),
    getAssignableUsers: (vpId) => api.get(`/users/vp/${vpId}/assignable-users`),
    getProfile: (id) => api.get(`/users/${id}/profile`),
    getRecentNotes: (id, params) => api.get(`/users/${id}/recent-notes`, { params }),
    // Rôle Auditeur (flag additif)
    setAuditorAccess: (id, isAuditor) => api.put(`/admin/users/${id}/auditor-access`, { is_auditor: isAuditor }),
    getAuditors: () => api.get('/auditors'),
};

// Profile API
export const profileAPI = {
    get: () => api.get('/profile'),
    update: (data) => api.put('/profile', data),
    uploadPicture: (pictureData) => api.post('/profile/picture', { picture: pictureData }),
    changePassword: (data) => api.put('/profile/password', data),
};

// Admin Users API
export const adminUsersAPI = {
    getAll: (includeInactive = false) => api.get('/admin/users', { params: { include_inactive: includeInactive } }),
    create: (data) => api.post('/admin/users', data),
    updateStatus: (userId, data) => api.put(`/admin/users/${userId}/status`, data),
    updateRole: (userId, role) => api.put(`/admin/users/${userId}/role`, { role }),
    updateCommissionRates: (userId, data) => api.put(`/admin/users/${userId}/commission-rates`, data),
    // Admin Photo Management
    updateUserPhoto: (userId, pictureData) => api.put(`/admin/users/${userId}/photo`, { picture: pictureData }),
    updateTeamPhoto: (teamId, pictureData) => api.put(`/admin/teams/${teamId}/photo`, { picture: pictureData }),
    updateWallCover: (wallType, teamId, pictureData) => api.put(`/admin/walls/${wallType}/cover`, { picture: pictureData }, { params: { team_id: teamId } }),
    // Recréer utilisateurs test
    recreateTestUsers: () => api.post('/admin/recreate-test-users'),
};

// Teams API
export const teamsAPI = {
    getAll: () => api.get('/teams'),
    getAllV2: () => api.get('/teams-v2'),
    getById: (id) => api.get(`/teams/${id}`),
    create: (data) => api.post('/teams', data),
    update: (id, data) => api.put(`/teams/${id}`, data),
    delete: (id) => api.delete(`/teams/${id}`),
    addMember: (teamId, data) => api.post(`/teams/${teamId}/members`, data),
    removeMember: (teamId, userId) => api.delete(`/teams/${teamId}/members/${userId}`),
    updateMemberCommission: (teamId, userId, rate) => api.put(`/teams/${teamId}/members/${userId}/commission`, { commission_rate: rate }),
};

// Company Types API
export const companyTypesAPI = {
    getAll: () => api.get('/company-types'),
};

// Prospect Classes API (new classification system)
export const prospectClassesAPI = {
    getAll: () => api.get('/prospect-classes'),
};

// Copropriétés (sous-dossiers autonomes d'un prospect Syndic)
export const coproprietesAPI = {
    list: (prospectId) => api.get(`/prospects/${prospectId}/coproprietes`),
    add: (prospectId, data) => api.post(`/prospects/${prospectId}/coproprietes`, data),
    remove: (prospectId, coproId) => api.delete(`/prospects/${prospectId}/coproprietes/${coproId}`),
};

// Sale Types API
export const saleTypesAPI = {
    getAll: () => api.get('/sale-types'),
};

// Prospects API
// Hashtags # par module sur les dossiers
export const hashtagsAPI = {
    list: () => api.get('/hashtags'),
    create: (data) => api.post('/hashtags', data),
    update: (id, data) => api.put(`/hashtags/${id}`, data),
    remove: (id) => api.delete(`/hashtags/${id}`),
    getForProspect: (prospectId) => api.get(`/prospects/${prospectId}/hashtags`),
    setForProspect: (prospectId, module, hashtagIds) =>
        api.put(`/prospects/${prospectId}/hashtags`, { module, hashtag_ids: hashtagIds }),
};

// Compteur d'implication
export const involvementAPI = {
    scores: (windowDays = 30) => api.get('/involvement/scores', { params: { window_days: windowDays } }),
    userDetail: (userId, windowDays = 30) => api.get(`/involvement/users/${userId}`, { params: { window_days: windowDays } }),
    aiAnalysis: (userId, windowDays = 30) => api.post(`/involvement/users/${userId}/ai-analysis`, null, { params: { window_days: windowDays } }),
    history: (userId, days = 90) => api.get(`/involvement/users/${userId}/history`, { params: { days } }),
    weeklyRecap: (windowDays = 30) => api.get('/involvement/weekly-recap', { params: { window_days: windowDays } }),
    exportRecap: (format, windowDays = 30, team) => api.get('/involvement/weekly-recap/export', { params: { format, window_days: windowDays, ...(team && team !== 'all' ? { team } : {}) }, responseType: 'blob' }),
    snapshot: () => api.post('/involvement/snapshot'),
};

// Instrumentation d'activité (Impact Score)
export const activityAPI = {
    heartbeat: (context = 'crm') => api.post('/activity/heartbeat', { context }),
    toolOpen: (toolId, label) => api.post('/activity/tool-open', { tool_id: toolId, label }),
};

// Documents utiles à vos prospections (admin téléverse, tous téléchargent)
export const usefulDocsAPI = {
    list: () => api.get('/useful-documents'),
    create: (formData) => api.post('/useful-documents', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
    update: (id, formData) => api.patch(`/useful-documents/${id}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
    download: (id) => api.get(`/useful-documents/${id}/download`, { responseType: 'blob' }),
    remove: (id) => api.delete(`/useful-documents/${id}`),
    unreadCount: () => api.get('/useful-documents/unread-count'),
    markSeen: (data) => api.post('/useful-documents/mark-seen', data),
};

// 📁 Documents de présentation client — miroir de useful-documents (fév 2026)
// Mêmes règles : admin téléverse/modifie/supprime, tous voient et téléchargent.
export const clientDocsAPI = {
    list: () => api.get('/client-documents'),
    create: (formData) => api.post('/client-documents', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
    update: (id, formData) => api.patch(`/client-documents/${id}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
    download: (id) => api.get(`/client-documents/${id}/download`, { responseType: 'blob' }),
    remove: (id) => api.delete(`/client-documents/${id}`),
    unreadCount: () => api.get('/client-documents/unread-count'),
    markSeen: () => api.post('/client-documents/mark-seen'),
};

// Documents Travaux d’optimisation (Études / Devis) rattachés à un dossier
export const chantierDocsAPI = {
    list: (prospectId, category) => api.get(`/prospects/${prospectId}/chantier-docs`, { params: category ? { category } : {} }),
    create: (prospectId, formData) => api.post(`/prospects/${prospectId}/chantier-docs`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
    download: (docId) => api.get(`/chantier-docs/${docId}/download`, { responseType: 'blob' }),
    remove: (docId) => api.delete(`/chantier-docs/${docId}`),
};

// Demandes officielles — VP → Dirigeants (Jérémy / Morgan / Laurent)
export const officialRequestsAPI = {
    config: () => api.get('/official-requests/config'),
    list: () => api.get('/official-requests'),
    get: (id) => api.get(`/official-requests/${id}`),
    create: (formData) => api.post('/official-requests', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
    update: (id, payload) => api.patch(`/official-requests/${id}`, payload),
    markRead: (id) => api.post(`/official-requests/${id}/mark-read`),
    downloadAttachment: (reqId, attId) => api.get(`/official-requests/${reqId}/attachments/${attId}`, { responseType: 'blob' }),
};

// Jauge "Développement Valnexo" (Dirigeants uniquement)
export const devFundAPI = {
    get: (params) => api.get('/dashboard/dev-fund', { params }),
};


export const prospectsAPI = {
    getAll: (params, config = {}) => api.get('/prospects', { params, ...config }),
    // Endpoint ultra-léger pour selects/pickers (nouvelle tâche/événement) — ~10-30x plus petit
    getPicker: (config = {}) => api.get('/prospects/picker', config),
    // Payload allégé pour les dashboards (Pilotage V2 / VP dissocié) — bien plus rapide
    getDashboardLight: (config = {}) => api.get('/dashboard/prospects-light', config),
    // Nouvelle API paginée avec métadonnées
    getPaginated: (params) => api.get('/prospects/paginated', { params }),
    // Export Excel
    exportExcel: (params) => api.get('/prospects/export/excel', { params, responseType: 'blob' }),
    getById: (id) => api.get(`/prospects/${id}`),
    create: (data) => api.post('/prospects', data),
    update: (id, data) => api.put(`/prospects/${id}`, data),
    delete: (id, confirmName) => api.delete(`/prospects/${id}`, { params: { confirm_name: confirmName } }),
    // Corbeille (soft-delete / restauration) — administrateurs
    getTrash: () => api.get('/admin/prospects/trash'),
    restore: (id) => api.post(`/prospects/${id}/restore`),
    restoreAll: () => api.post('/admin/prospects/restore-all'),
    permanentDelete: (id, confirmName) => api.delete(`/prospects/${id}/permanent`, { params: { confirm_name: confirmName } }),
    deletionAudit: (q) => api.get('/admin/prospects/deletion-audit', { params: q ? { q } : {} }),
    // Auditeur du dossier (chantier uniquement)
    setAuditor: (id, auditeurId) => api.put(`/prospects/${id}/auditor`, { auditeur_id: auditeurId || null }),
    getAuditedDossiers: () => api.get('/audited-dossiers'),
    // Override manuel des commissions (admin_full) — Courtage
    setCommissionOverride: (id, data) => api.post(`/prospects/${id}/commission-override`, data),
    resetCommissionOverride: (id) => api.delete(`/prospects/${id}/commission-override`),
    updateStage: (id, stage) => api.put(`/prospects/${id}/stage?stage=${stage}`),
    updateEasyPowerStage: (id, stage) => api.put(`/prospects/${id}/easy-power-stage?stage=${stage}`),
    updateChantierStage: (id, stage) => api.put(`/prospects/${id}/chantier-stage?stage=${stage}`),
    createChantierPipeline: (id) => api.post(`/prospects/${id}/create-chantier`),
    createEasyPowerPipeline: (id) => api.post(`/prospects/${id}/create-easy-power`),
    // Nouvelle route de changement d'étape avec historique
    changeStage: (id, pipelineType, newStage, comment, lossReason) => {
        let url = `/prospects/${id}/change-stage?pipeline_type=${pipelineType}&new_stage=${newStage}`;
        if (comment) url += `&comment=${encodeURIComponent(comment)}`;
        if (lossReason) url += `&loss_reason=${encodeURIComponent(lossReason)}`;
        return api.put(url);
    },
    getStageHistory: (id, pipelineType) => 
        api.get(`/prospects/${id}/stage-history${pipelineType ? '?pipeline_type=' + pipelineType : ''}`),
    // P0 Commission endpoints (par pipeline)
    getCommissions: (id) => api.get(`/prospects/${id}/commissions`),
    updateCommissions: (id, pipelineType, data) => api.put(`/prospects/${id}/commissions/${pipelineType}`, data),
    // NOUVEAU: Calcul commission avec split 50/50
    calculateCommission: (id, pipelineType, data) => api.post(`/prospects/${id}/commissions/${pipelineType}/calculate`, data),
    // NOUVEAU: Modifier Boost VP (Full Admin only)
    updateBoostVP: (id, pipelineType, boostVp, reason) => 
        api.put(`/prospects/${id}/commissions/${pipelineType}/boost?boost_vp=${boostVp}&reason=${encodeURIComponent(reason)}`),
    validateCommissions: (id, pipelineType, comment) => api.post(`/prospects/${id}/commissions/${pipelineType}/validate`, { comment }),
    unlockCommissions: (id, pipelineType, comment) => api.post(`/prospects/${id}/commissions/${pipelineType}/unlock`, { comment }),
    getCommissionsAudit: (id, pipelineType) => api.get(`/prospects/${id}/commissions/${pipelineType}/audit`),
    // Pièces jointes
    getAttachments: (id) => api.get(`/prospects/${id}/attachments`),
    uploadAttachment: (id, formData) => api.post(`/prospects/${id}/attachments`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
    }),
    // Documents Travaux d’optimisation (Rénovation Globale)
    uploadDocument: (id, formData) => api.post(`/prospects/${id}/chantier/documents`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
    }),
    // Prolongements (renouvellements annuels Courtage)
    getProlongements: (id) => api.get(`/prospects/${id}/prolongements`),
    createProlongement: (id, data) => api.post(`/prospects/${id}/prolongements`, data),
    // RIB Download
    downloadRib: (id) => api.get(`/prospects/${id}/easy-power-rib/download`, { responseType: 'blob', timeout: 120000, skipRetry: true }),
    // Mur du dossier (messages + @mentions), indépendant par module
    getNotes: (id, module) => api.get(`/prospects/${id}/notes`, module ? { params: { module } } : undefined),
    addNote: (id, data) => api.post(`/prospects/${id}/notes`, data),
    deleteNote: (id, noteId) => api.delete(`/prospects/${id}/notes/${noteId}`),
    updateNote: (id, noteId, data) => api.patch(`/prospects/${id}/notes/${noteId}`, data),
    // Réactions « façon Facebook » sur un message du mur (1 réaction/personne)
    reactToNote: (id, noteId, reaction) => api.post(`/prospects/${id}/notes/${noteId}/react`, { reaction }),
    getMentionableUsers: (id, module) => api.get(`/prospects/${id}/mentionable-users`, { params: module ? { module } : {} }),
    // Prochaine action planifiée du dossier (badge fiche + modale de sortie)
    getNextAction: (id) => api.get(`/prospects/${id}/next-action`),
    markWallRead: (id, module) => api.post(`/prospects/${id}/wall/mark-read`, null, { params: { module } }),
    // Sous-étapes Travaux d’optimisation (Lot 4) au niveau dossier
    updateChantierSubsteps: (id, substeps) => api.put(`/prospects/${id}/chantier-substeps`, { substeps }),
};

// Mur des dossiers — pastilles "nouveaux messages" (synthèse + batch par liste)
export const wallAPI = {
    unreadSummary: () => api.get('/wall/unread-summary'),
    unreadBatch: (prospectIds) => api.post('/wall/unread-batch', { prospect_ids: prospectIds }),
    markRead: (prospectId, module) => api.post(`/prospects/${prospectId}/wall/mark-read`, null, { params: { module } }),
    // Modèles de messages partagés (lecture pour tous, écriture pour admins)
    listTemplates: () => api.get('/wall/templates'),
    createTemplate: (data) => api.post('/wall/templates', data),
    updateTemplate: (id, data) => api.patch(`/wall/templates/${id}`, data),
    deleteTemplate: (id) => api.delete(`/wall/templates/${id}`),
};

// Boîte à Idées Valnexo — espace d'amélioration continue
export const ideasAPI = {
    meta: () => api.get('/ideas/meta'),
    create: (data) => api.post('/ideas', data),
    mine: () => api.get('/ideas/mine'),
    get: (id) => api.get(`/ideas/${id}`),
    list: (params) => api.get('/ideas', { params }),
    stats: () => api.get('/ideas/stats'),
    assignableUsers: () => api.get('/ideas/assignable-users'),
    setStatus: (id, data) => api.patch(`/ideas/${id}/status`, data),
    addComment: (id, content) => api.post(`/ideas/${id}/comments`, { content }),
    setScoring: (id, impact_score, ease_score) => api.patch(`/ideas/${id}/scoring`, { impact_score, ease_score }),
    assign: (id, assigned_to) => api.patch(`/ideas/${id}/assign`, { assigned_to }),
};

// Prolongements API (standalone)
export const prolongementsAPI = {
    validate: (id) => api.put(`/prolongements/${id}/validate`),
};

// Commission simulation API
export const commissionSimulationAPI = {
    simulate: (montantGlobalHt, tauxVp = 0, boostVp = 0, tauxApporteur = 0) => 
        api.get(`/commissions/simulation?montant_global_ht=${montantGlobalHt}&taux_vp=${tauxVp}&boost_vp=${boostVp}&taux_apporteur=${tauxApporteur}`),
};

// Attachments API (standalone)
export const attachmentsAPI = {
    download: (id) => api.get(`/attachments/${id}/download`, { responseType: 'blob', timeout: 120000, skipRetry: true }),
    delete: (id) => api.delete(`/attachments/${id}`),
};

// Search API (entreprises et géolocalisation)
export const searchAPI = {
    companies: (query) => api.get('/search/companies', { params: { query } }),
    citiesByPostalCode: (postalCode) => api.get('/search/cities', { params: { postal_code: postalCode } }),
    // Recherche universelle cross-modules
    universal: (query, limit = 10) => api.get('/search/universal', { params: { q: query, limit } }),
};

// Invoices API
export const invoicesAPI = {
    getAll: (params) => api.get('/invoices', { params }),
    getById: (id) => api.get(`/invoices/${id}`),
    getPendingCommissions: (params) => api.get('/invoices/pending-commissions', { params }),
    create: (data) => api.post('/invoices', data),
    createCreditNote: (data) => api.post('/invoices/credit-note', data),
    issue: (id) => api.put(`/invoices/${id}/issue`),
    markPaid: (id) => api.put(`/invoices/${id}/pay`),
    delete: (id) => api.delete(`/invoices/${id}`),
    downloadPdf: async (id) => {
        const response = await api.get(`/invoices/${id}/pdf`, { responseType: 'blob' });
        return response;
    },
    // Nouvelles fonctions de recherche
    search: (params) => api.get('/invoices/search', { params }),
    searchByClient: (prospectId) => api.get(`/invoices/search/by-client/${prospectId}`),
    searchMyTeam: () => api.get('/invoices/search/my-team'),
    generateInvoiceNumber: (prospectId, issuerUserId) => api.post('/billing/generate-invoice-number', { 
        prospect_id: prospectId, 
        issuer_user_id: issuerUserId 
    }),
};

// Sales API
export const salesAPI = {
    getAll: (params) => api.get('/sales', { params }),
    create: (data) => api.post('/sales', data),
    getByProspect: (prospectId) => api.get('/sales', { params: { prospect_id: prospectId } }),
};

// Pipeline API
export const pipelineAPI = {
    getStages: () => api.get('/pipeline/stages'),
};

// Attribution commerciale API
export const attributionAPI = {
    getVPsDisponibles: () => api.get('/dossier/vps-disponibles'),
    getApporteursDisponibles: (vpId) => api.get(`/dossier/apporteurs-disponibles/${vpId}`),
};

// Dashboard API
export const dashboardAPI = {
    getStats: () => api.get('/dashboard/stats'),
    getRestantsStats: (period = 'month', pipeline = 'both') => 
        api.get(`/dashboard/restants-stats?period=${period}&pipeline=${pipeline}`),
    getOverdueProspects: (params) => api.get('/dashboard/overdue-prospects', { params }),
    // Dossiers non sécurisés (actifs sans prochaine action planifiée)
    getUnsecuredProspects: (params) => api.get('/dashboard/unsecured-prospects', { params }),
    // Pilotage V2 — vue d'ensemble pilotable (Admin / VP).
    // Timeout étendu (60s) : la 1re charge à froid (gros volume / connexion mobile) peut
    // dépasser le timeout global de 15s et déclencher un faux échec. Le payload est compressé
    // (GZip backend) donc les charges suivantes restent rapides.
    getPilotage: (params) => api.get('/dashboard/pilotage', { params, timeout: 60000 }),
    // Fil de commissions parrain — récemment gagnées + à aller chercher (polling 30s)
    getParrainFeed: (params) => api.get('/dashboard/pilotage/parrain-feed', { params, timeout: 30000 }),
    // Prime de secteur Courtage (VP = son secteur ; admin = tout VP via vp_id)
    getPrimeSecteur: (params) => api.get('/dashboard/prime-secteur', { params }),
    getPrimeSecteurHistory: (params) => api.get('/dashboard/prime-secteur/history', { params }),
    getPrimeSecteurOverview: (params) => api.get('/dashboard/prime-secteur/overview', { params }),
};

// Activities API
export const activitiesAPI = {
    getAll: (limit = 50) => api.get('/activities', { params: { limit } }),
};

// Commissions API
export const commissionsAPI = {
    getMy: () => api.get('/commissions'),
    getMySummary: () => api.get('/commissions/my-summary'),
    getStats: (params) => api.get('/commissions/stats', { params }),
};

// VP Commission Settings API
export const vpCommissionAPI = {
    getSettings: () => api.get('/vp/commission-settings'),
    updateSettings: (data) => api.put('/vp/commission-settings', data),
    adminUpdateVP: (vpId, data) => api.put(`/admin/vp/${vpId}/commission-settings`, data),
};

// Ledger API (New Commission Engine)
export const ledgerAPI = {
    // Commission Fournisseur
    getCommissions: (params) => api.get('/ledger/commissions', { params }),
    getCommission: (id) => api.get(`/ledger/commissions/${id}`),
    createCommission: (data) => api.post('/ledger/commissions', data),
    updateCommission: (id, data) => api.put(`/ledger/commissions/${id}`, data),
    simulateCommission: (id) => api.get(`/ledger/commissions/${id}/simulate`),
    validateCommission: (id, data) => api.post(`/ledger/commissions/${id}/validate`, data || {}),
    
    // Ledger Entries
    getLedgerEntries: (params) => api.get('/ledger/entries', { params }),
    
    // Overrides
    getOverride: (scopeType, scopeId) => api.get(`/ledger/overrides/${scopeType}/${scopeId}`),
    createOverride: (data) => api.post('/ledger/overrides', data),
    deleteOverride: (id) => api.delete(`/ledger/overrides/${id}`),
    
    // VP Profile
    getVPProfile: (vpId) => api.get(`/ledger/vp/${vpId}/profile`),
    updateVPProfile: (vpId, data) => api.put(`/ledger/vp/${vpId}/profile`, data),
    
    // Ledger Invoices
    getLedgerInvoices: (params) => api.get('/ledger/invoices', { params }),
    generateInvoices: (data) => api.post('/ledger/invoices/generate', data),
    emitInvoice: (id) => api.put(`/ledger/invoices/${id}/emit`),
    payInvoice: (id, data) => api.put(`/ledger/invoices/${id}/pay`, data || {}),
};

// Company Info API
export const companyInfoAPI = {
    get: () => api.get('/company-info'),
    update: (data) => api.put('/company-info', data),
};

// Notifications API
export const notificationsAPI = {
    getAll: (limit = 50) => api.get('/notifications', { params: { limit } }),
    markRead: (id) => api.put(`/notifications/${id}/read`),
    markAllRead: () => api.put('/notifications/read-all'),
    markSeen: () => api.put('/notifications/seen'),
    markUnread: (id) => api.put(`/notifications/${id}/unread`),
    delete: (id) => api.delete(`/notifications/${id}`),
};

// Notification Settings API
export const notificationSettingsAPI = {
    get: () => api.get('/settings/notifications'),
    update: (data) => api.put('/settings/notifications', data),
    updateForUser: (userId, data) => api.put(`/settings/notifications/${userId}`, data),
    getAll: () => api.get('/settings/notifications/all'),
};

// Company Settings API (Valnexo)
export const companySettingsAPI = {
    get: () => api.get('/settings/company'),
    update: (data) => api.put('/settings/company', data),
};

// AT Service Settings API
export const atServiceSettingsAPI = {
    get: () => api.get('/settings/at-service'),
    update: (data) => api.put('/settings/at-service', data),
};

// Payeurs API
export const payersAPI = {
    getAll: () => api.get('/payers'),
    getById: (id) => api.get(`/payers/${id}`),
    create: (data) => api.post('/payers', data),
    update: (id, data) => api.put(`/payers/${id}`, data),
    delete: (id) => api.delete(`/payers/${id}`),
};

// Tasks API
export const tasksAPI = {
    getAll: (params) => api.get('/tasks', { params }),
    getById: (id) => api.get(`/tasks/${id}`),
    create: (data) => api.post('/tasks', data),
    update: (id, data) => api.put(`/tasks/${id}`, data),
    delete: (id) => api.delete(`/tasks/${id}`),
    getStats: () => api.get('/tasks/stats/summary'),
    remind: (id) => api.post(`/tasks/${id}/remind`),
    bulkDelete: (taskIds) => api.post('/tasks/bulk-delete', { task_ids: taskIds }),
    createFilRouge: (data) => api.post('/tasks/fil-rouge', data),
    deleteFilRouge: (filRougeId) => api.delete(`/tasks/fil-rouge/${filRougeId}`),
};

// Teams V2 API (Multi-équipes)
export const teamsV2API = {
    getAll: () => api.get('/teams-v2'),
    getById: (id) => api.get(`/teams-v2/${id}`),
    create: (data) => api.post('/teams-v2', data),
    update: (id, data) => api.put(`/teams-v2/${id}`, data),
    delete: (id) => api.delete(`/teams-v2/${id}`),
    addMember: (teamId, data) => api.post(`/teams-v2/${teamId}/members`, data),
    removeMember: (teamId, userId) => api.delete(`/teams-v2/${teamId}/members/${userId}`),
    addManager: (teamId, data) => api.post(`/teams-v2/${teamId}/managers`, data),
    removeManager: (teamId, userId) => api.delete(`/teams-v2/${teamId}/managers/${userId}`),
    // Commissions des membres (P0)
    getMemberCommission: (teamId, userId) => api.get(`/teams-v2/${teamId}/members/${userId}/commission`),
    updateMemberCommission: (teamId, userId, data) => api.put(`/teams-v2/${teamId}/members/${userId}/commission`, data),
    // Calculateur de commission
    calculateCommission: (teamId, params) => api.get(`/teams-v2/${teamId}/commission-calculator`, { params }),
    // Auditeur d'office (Travaux d’optimisation) — rattrape les dossiers existants + applique au futur
    setChantierAuditor: (teamId, auditeur_id) => api.put(`/teams-v2/${teamId}/chantier-auditor`, { auditeur_id }),
};

// Calendar API
export const calendarAPI = {
    getEvents: (params) => api.get('/calendar/events', { params }),
    getEvent: (id) => api.get(`/calendar/events/${id}`),
    createEvent: (data) => api.post('/calendar/events', data),
    updateEvent: (id, data) => api.put(`/calendar/events/${id}`, data),
    deleteEvent: (id) => api.delete(`/calendar/events/${id}`),
    checkAvailability: (data) => api.post('/calendar/availability/check', data),
    getMyEvents: (params) => api.get('/calendar/my-events', { params }),
};

// Google Agenda (OAuth + sync)
export const googleCalendarAPI = {
    status: () => api.get('/oauth/calendar/status'),
    login: (origin) => api.get('/oauth/calendar/login', { params: { origin } }),
    disconnect: () => api.post('/oauth/calendar/disconnect'),
    sync: (full = false) => api.post('/oauth/calendar/sync', {}, { params: full ? { full: true } : {}, timeout: 120000 }),
    backfillCount: () => api.get('/oauth/calendar/backfill-count'),
    backfill: () => api.post('/oauth/calendar/backfill', {}, { timeout: 180000 }),
};

// Réseau Parrainage (module indépendant)
export const parrainageAPI = {
    getVpAccess: (vpId) => api.get(`/parrainage/vp/${vpId}/access`),
    setVpAccess: (vpId, enabled) => api.put(`/parrainage/vp/${vpId}/access`, { enabled }),
    listVps: () => api.get('/parrainage/vps'),
    getSettings: () => api.get('/parrainage/settings'),
    updateSettings: (data) => api.put('/parrainage/settings', data),
    listParrains: (vpId) => api.get('/parrainage/parrains', { params: vpId ? { vp_id: vpId } : {} }),
    getTree: (vpId) => api.get('/parrainage/tree', { params: vpId ? { vp_id: vpId } : {} }),
    createParrain: (data) => api.post('/parrainage/parrains', data),
    updateParrain: (id, data) => api.put(`/parrainage/parrains/${id}`, data),
    moveParrain: (id, data) => api.put(`/parrainage/parrains/${id}/move`, data),
    setStatus: (id, isActive) => api.put(`/parrainage/parrains/${id}/status`, { is_active: isActive }),
    deleteParrain: (id) => api.delete(`/parrainage/parrains/${id}`),
    resendInvite: (id) => api.post(`/parrainage/parrains/${id}/resend-invite`),
    getAudit: () => api.get('/parrainage/audit'),
    // Espace parrain : coordonnées, règlement, contrat
    getMySpace: () => api.get('/parrainage/me/space'),
    updateMyProfile: (data) => api.put('/parrainage/me/profile', data),
    uploadMyContract: (formData) => api.post('/parrainage/me/contract', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
    downloadMyContract: () => api.get('/parrainage/me/contract/download', { responseType: 'blob' }),
    downloadTemplate: () => api.get('/parrainage/contract/template/download', { responseType: 'blob' }),
    uploadTemplate: (formData) => api.post('/parrainage/contract-template', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
    downloadParrainContract: (id) => api.get(`/parrainage/parrains/${id}/contract/download`, { responseType: 'blob' }),
    // Phase 2b — Commissions Parrains
    listCommissions: (vpId) => api.get('/parrainage/commissions', { params: vpId ? { vp_id: vpId } : {} }),
    getProspectCommissions: (prospectId) => api.get(`/prospects/${prospectId}/parrain-commissions`),
    // Phase 3 — Tableau de bord, Classement, Activité, Objectifs
    getDashboard: (params) => api.get('/parrainage/dashboard', { params: params || {} }),
    getLeaderboard: (params) => api.get('/parrainage/leaderboard', { params: params || {} }),
    getActivity: (params) => api.get('/parrainage/activity', { params: params || {} }),
    getObjectives: (vpId) => api.get('/parrainage/objectives', { params: vpId ? { vp_id: vpId } : {} }),
    setObjective: (data) => api.post('/parrainage/objectives', data),
};

// Prospect Teams API
export const prospectTeamsAPI = {
    getTeams: (prospectId) => api.get(`/prospects/${prospectId}/teams`),
    assignTeams: (prospectId, teamIds) => api.put(`/prospects/${prospectId}/teams`, teamIds),
};

// Seed API
export const seedAPI = {
    seed: () => api.post('/seed'),
};

// Stats Commerciales API
export const statsAPI = {
    getMyStats: (params) => api.get('/stats/me', { params }),
    getUserStats: (userId, params) => api.get(`/stats/user/${userId}`, { params }),
    getHierarchyStats: (params) => api.get('/stats/hierarchy', { params }),
    getTeamStats: (teamId, params) => api.get(`/stats/team/${teamId}`, { params }),
    getGlobalStats: (params) => api.get('/stats/global', { params }),
    // Nouveaux endpoints - Filtres avancés
    getFilterOptions: () => api.get('/stats/filters/options'),
    getAdvancedStats: (params) => api.get('/stats/advanced', { params }),
    getRankings: (params) => api.get('/stats/rankings', { params }),
    getTeamMembersStats: (teamId, params) => api.get(`/stats/team/${teamId}/members`, { params }),
    // Evolution / Tendances
    getEvolution: (params) => api.get('/stats/evolution', { params }),
    // Commissions en cours (motivation)
    getPendingCommissions: () => api.get('/stats/pending-commissions'),
    // Pilotage membre - Dashboard détaillé
    getMemberDashboard: (memberId) => api.get(`/stats/member-dashboard/${memberId}`),
};

// Academy API (Formation)
export const academyAPI = {
    // Modules
    getModules: (category) => api.get('/academy/modules', { params: { category } }),
    getModule: (id) => api.get(`/academy/modules/${id}`),
    getNextModule: (id) => api.get(`/academy/modules/${id}/next`),
    createModule: (data) => api.post('/academy/modules', data),
    updateModule: (id, data) => api.put(`/academy/modules/${id}`, data),
    deleteModule: (id) => api.delete(`/academy/modules/${id}`),
    
    // Contents
    addContent: (moduleId, data) => api.post(`/academy/modules/${moduleId}/contents`, data),
    updateContent: (moduleId, contentId, data) => api.put(`/academy/modules/${moduleId}/contents/${contentId}`, data),
    deleteContent: (moduleId, contentId) => api.delete(`/academy/modules/${moduleId}/contents/${contentId}`),
    
    // Trames & supports (ressources fichiers du module)
    getModuleResources: (moduleId) => api.get(`/academy/modules/${moduleId}/resources`),
    uploadModuleResource: (moduleId, formData, onUploadProgress) => api.post(
        `/academy/modules/${moduleId}/resources`, formData,
        { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000, onUploadProgress }
    ),
    deleteModuleResource: (moduleId, resourceId) => api.delete(`/academy/modules/${moduleId}/resources/${resourceId}`),
    getResourceFile: (moduleId, resourceId, download = 0) => api.get(
        `/academy/modules/${moduleId}/resources/${resourceId}/file`,
        { params: { download }, responseType: 'blob', timeout: 120000 }
    ),
    
    // Quiz
    createQuiz: (moduleId, data) => api.post(`/academy/modules/${moduleId}/quiz`, data),
    submitQuiz: (moduleId, answers) => api.post(`/academy/modules/${moduleId}/quiz/submit`, { answers }),
    
    // Progress
    updateProgress: (moduleId, data) => api.post(`/academy/modules/${moduleId}/progress`, data),
    getMyProgress: () => api.get('/academy/my-progress'),
    
    // Admin
    getAdminDashboard: () => api.get('/academy/admin/dashboard'),
    getUserProgress: (userId) => api.get(`/academy/admin/user/${userId}/progress`),
    getModuleUsersProgress: (moduleId) => api.get(`/academy/admin/module/${moduleId}/users-progress`),
    forceViewed: (data) => api.post('/academy/admin/force-viewed', data),
    recoverVideoProgress: (data) => api.post('/academy/admin/recover-video-progress', data),
    
    // Categories
    getCategories: () => api.get('/academy/categories'),
};

// Pipelines API (Dynamic Pipeline Management)
export const pipelinesAPI = {
    getAll: () => api.get('/pipelines'),
    getById: (id) => api.get(`/pipelines/${id}`),
    getStages: (id) => api.get(`/pipelines/${id}/stages`),
    create: (data) => api.post('/pipelines', data),
    update: (id, data) => api.put(`/pipelines/${id}`, data),
    delete: (id) => api.delete(`/pipelines/${id}`),
    addStage: (pipelineId, stageData) => api.post(`/pipelines/${pipelineId}/stages`, stageData),
};

// Support API (Support & Amélioration)
export const supportAPI = {
    // Création de tickets
    createBugTicket: (data) => api.post('/support/bug', data),
    createAmeliorationTicket: (data) => api.post('/support/amelioration', data),
    createBugWithAttachment: (formData) => api.post('/support/bug/with-attachment', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
    }),
    createAmeliorationWithAttachment: (formData) => api.post('/support/amelioration/with-attachment', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
    }),
    // Mes tickets
    getMyTickets: () => api.get('/support/my-tickets'),
    // Listes
    getModules: () => api.get('/support/modules'),
    getPipelines: () => api.get('/support/pipelines-list'),
    // Admin
    adminGetAllTickets: (params) => api.get('/support/admin/tickets', { params }),
    adminGetTicket: (id) => api.get(`/support/admin/tickets/${id}`),
    adminUpdateStatus: (id, data) => api.put(`/support/admin/tickets/${id}/status`, data),
    adminGetStats: () => api.get('/support/admin/stats'),
    adminGetAttachment: (id) => api.get(`/support/admin/attachment/${id}`),
};

// Feed API (Fil d'actualité)
export const feedAPI = {
    getUnreadCount: () => api.get('/feed/unread-count'),
    markAllRead: () => api.post('/feed/mark-all-read'),
};

// Courtage Data API
export const easyPowerAPI = {
    updateData: (prospectId, data) => api.put(`/prospects/${prospectId}/easy-power-data`, data),
    autoFill: (prospectId) => api.post(`/prospects/${prospectId}/easy-power-data/auto-fill`),
};

// RGPD Mandat API
export const rgpdAPI = {
    updateMandat: (prospectId, data) => api.put(`/prospects/${prospectId}/rgpd-mandat`, data),
    checkStatus: (prospectId) => api.get(`/prospects/${prospectId}/rgpd-check`),
};

// API Clients (prospects finalisés)
export const clientsAPI = {
    getAll: (params) => api.get('/clients', { params }),
    getById: (id) => api.get(`/clients/${id}`),
    getStats: () => api.get('/clients/stats'),
};

// API EP Archives (dossiers transmis)
export const epArchivesAPI = {
    getAll: (params) => api.get('/ep-archives', { params }),
    getById: (id) => api.get(`/ep-archives/${id}`),
    download: (id) => api.get(`/ep-archives/${id}/download`, { responseType: 'blob' }),
    sendEmail: (id, data) => api.post(`/ep-archives/${id}/send-email`, data),
};

// Demandes de suppression API
export const deletionRequestsAPI = {
    getAll: (status) => api.get('/deletion-requests', { params: { status } }),
    create: (prospectId, motif) => api.post('/deletion-requests', { prospect_id: prospectId, motif }),
    approve: (requestId) => api.put(`/deletion-requests/${requestId}/approve`),
    reject: (requestId, reason) => api.put(`/deletion-requests/${requestId}/reject?reason=${encodeURIComponent(reason)}`),
};

export const chantiersAPI = {
    list: (prospectId) => api.get(`/prospects/${prospectId}/chantiers`),
    create: (prospectId, data) => api.post(`/prospects/${prospectId}/chantiers`, data),
    update: (prospectId, chantierId, data) => api.put(`/prospects/${prospectId}/chantiers/${chantierId}`, data),
    delete: (prospectId, chantierId) => api.delete(`/prospects/${prospectId}/chantiers/${chantierId}`),
    synthesis: (prospectId) => api.get(`/prospects/${prospectId}/chantiers/synthesis`),
};



// ============== Inside VNX — Réseau social interne (Phase 1) ==============
export const socialAPI = {
    feed: (params) => api.get('/social/feed', { params }),
    feedUpdates: (since) => api.get('/social/feed/updates', { params: { since } }),
    createPost: (data) => api.post('/social/posts', data),
    getPost: (id) => api.get(`/social/posts/${id}`),
    deletePost: (id) => api.delete(`/social/posts/${id}`),
    like: (id) => api.post(`/social/posts/${id}/like`),
    postLikes: (id) => api.get(`/social/posts/${id}/likes`),
    setPhotoTags: (postId, mediaId, tags) => api.put(`/social/posts/${postId}/media/${mediaId}/tags`, { tags }),
    comments: (id) => api.get(`/social/posts/${id}/comments`),
    addComment: (id, data) => api.post(`/social/posts/${id}/comments`, data),
    deleteComment: (id) => api.delete(`/social/comments/${id}`),
    reactComment: (id, reaction) => api.post(`/social/comments/${id}/react`, { reaction }),
    uploadMedia: (file, onUploadProgress, config = {}) => {
        const fd = new FormData();
        fd.append('file', file, file.name || 'media');
        return api.post('/social/media', fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 180000,
            onUploadProgress,
            ...config,
        });
    },
    downloadMedia: async (mediaId, filename) => {
        const res = await api.get(`/social/media/${mediaId}`, { params: { download: 1 }, responseType: 'blob' });
        const url = URL.createObjectURL(res.data);
        const a = document.createElement('a');
        a.href = url; a.download = filename || 'fichier';
        document.body.appendChild(a); a.click(); a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 1500);
    },
    userProfile: (id, params) => api.get(`/social/users/${id}/profile`, { params }),
    myProfile: () => api.get('/social/me/profile'),
    search: (q, semantic = false) => api.get('/social/search', { params: { q, semantic } }),
    hashtag: (tag, params) => api.get(`/social/hashtags/${tag}`, { params }),
    trending: () => api.get('/social/trending-hashtags'),
    mentionable: (q) => api.get('/social/mentionable', { params: { q } }),
    notifications: (limit = 30) => api.get('/social/notifications', { params: { limit } }),
    unreadCount: () => api.get('/social/notifications/unread-count'),
    readAll: () => api.post('/social/notifications/read-all'),
    crmSearch: (q) => api.get('/social/crm/search', { params: { q } }),
    crmPosts: (type, id) => api.get(`/social/crm/${type}/${id}/posts`),
    // Médiathèque (Community Manager + admins) — galerie de contenu à télécharger
    mediaLibrary: (params) => api.get('/social/media-library', { params }),
    taggableTeams: () => api.get('/social/taggable-teams'),
    mediaLibraryAuthors: () => api.get('/social/media-library/authors'),
    mediaSetStatus: (id, status) => api.patch(`/social/media-library/${id}/status`, { status }),
    mediaLibraryZip: async (mediaIds) => {
        const res = await api.post('/social/media-library/zip', { media_ids: mediaIds }, { responseType: 'blob', timeout: 300000 });
        const url = URL.createObjectURL(res.data);
        const a = document.createElement('a');
        a.href = url; a.download = 'inside-vnx-medias.zip';
        document.body.appendChild(a); a.click(); a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 1500);
    },
    // Stories (Phase 2)
    stories: () => api.get('/social/stories'),
    createStory: (data) => api.post('/social/stories', data),
    viewStory: (id) => api.post(`/social/stories/${id}/view`),
    storyViews: (id) => api.get(`/social/stories/${id}/views`),
    reactStory: (id, emoji) => api.post(`/social/stories/${id}/react`, { emoji }),
    replyStory: (id, content) => api.post(`/social/stories/${id}/reply`, { content }),
    deleteStory: (id) => api.delete(`/social/stories/${id}`),
    // "À la une" (highlights — stories épinglées au profil)
    highlightStory: (id, label) => api.post(`/social/stories/${id}/highlight`, { label }),
    userHighlights: (userId) => api.get(`/social/users/${userId}/highlights`),
    deleteHighlight: (hid) => api.delete(`/social/highlights/${hid}`),
    // Messagerie (DM + groupes de discussion)
    conversations: () => api.get('/social/conversations'),
    createConversation: (data) => api.post('/social/conversations', data),
    getConversation: (id) => api.get(`/social/conversations/${id}`),
    messages: (id, params) => api.get(`/social/conversations/${id}/messages`, { params }),
    sendMessage: (id, data, config = {}) => api.post(`/social/conversations/${id}/messages`, data, config),
    reactMessage: (cid, mid, emoji) => api.post(`/social/conversations/${cid}/messages/${mid}/react`, { emoji }),
    markConversationRead: (id) => api.post(`/social/conversations/${id}/read`),
    conversationsUnread: () => api.get('/social/conversations/unread-count'),
    // Groupes internes (feeds dédiés)
    groups: () => api.get('/social/groups'),
    createGroup: (data) => api.post('/social/groups', data),
    getGroup: (id) => api.get(`/social/groups/${id}`),
    joinGroup: (id) => api.post(`/social/groups/${id}/join`),
    leaveGroup: (id) => api.post(`/social/groups/${id}/leave`),
    addGroupMembers: (id, data) => api.post(`/social/groups/${id}/members`, data),
    groupFeed: (id, params) => api.get(`/social/groups/${id}/feed`, { params }),
    // IA (Phase 3)
    aiGeneratePost: (prompt, tone) => api.post('/social/ai/generate-post', { prompt, tone }),
    aiSummarize: (data) => api.post('/social/ai/summarize', data),
    aiTranslate: (text, target_lang) => api.post('/social/ai/translate', { text, target_lang }),
    // Modération (admins)
    banUser: (data) => api.post('/social/moderation/ban', data),
    unbanUser: (user_id) => api.post('/social/moderation/unban', { user_id }),
    bans: () => api.get('/social/moderation/bans'),
    // RGPD / CGU (acceptation obligatoire)
    rgpdStatus: () => api.get('/social/rgpd-status'),
    rgpdAccept: () => api.post('/social/rgpd-accept'),
};

export const pushAPI = {
    vapidPublicKey: () => api.get('/push/vapid-public-key'),
    subscribe: (data) => api.post('/push/subscribe', data),
    unsubscribe: (endpoint) => api.post('/push/unsubscribe', { endpoint }),
    test: () => api.post('/push/test'),
};

// URL de média Inside VNX (nécessite un fetch authentifié → voir composant AuthMedia)
export const socialMediaUrl = (mediaId) => `${API_URL}/social/media/${mediaId}`;

// ============== Relevé d'étude sur place (natif, hors-ligne) ==============
export const studyFormsAPI = {
    list: (prospectId) => api.get(`/study-forms/prospects/${prospectId}`),
    create: (prospectId, body = {}) => api.post(`/study-forms/prospects/${prospectId}`, body),
    get: (studyId) => api.get(`/study-forms/${studyId}`),
    update: (studyId, data) => api.put(`/study-forms/${studyId}`, { data }),
    validate: (studyId) => api.post(`/study-forms/${studyId}/validate`, {}, { timeout: 120000 }),
    remove: (studyId) => api.delete(`/study-forms/${studyId}`),
    deleteFile: (studyId, fileId) => api.delete(`/study-forms/${studyId}/files/${fileId}`),
    // Upload d'un fichier (timeout élevé, 1 fichier = 1 requête indépendante)
    uploadFile: (studyId, category, file, clientFileId) => {
        const fd = new FormData();
        fd.append('category', category);
        fd.append('file', file, file.name || 'fichier');
        if (clientFileId) fd.append('client_file_id', clientFileId);
        return api.post(`/study-forms/${studyId}/files`, fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 120000,
        });
    },
    // URL authentifiée non utilisable directement (token en header) → on récupère via fetch blob
    fileObjectUrl: async (studyId, fileId) => {
        const token = localStorage.getItem('valnexo_token');
        const res = await fetch(`${API_URL}/study-forms/${studyId}/files/${fileId}`, {
            headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error('fetch file failed');
        const blob = await res.blob();
        return URL.createObjectURL(blob);
    },
    pdfObjectUrl: async (studyId) => {
        const token = localStorage.getItem('valnexo_token');
        const res = await fetch(`${API_URL}/study-forms/${studyId}/pdf`, {
            headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error('fetch pdf failed');
        const blob = await res.blob();
        return URL.createObjectURL(blob);
    },
};

// Annonces / Nouveautés (bannières de fonctionnalités, adaptées au rôle)
export const announcementsAPI = {
    current: () => api.get('/announcements/current'),
    all: () => api.get('/announcements/all'),
    dismiss: (id) => api.post(`/announcements/${id}/dismiss`),
    reset: (id) => api.post(`/announcements/${id}/reset`),
};

// Hub Projets (kanban multi-espaces, thèmes paramétrables, pipelines custom)
export const hubAPI = {
    access: () => api.get('/hub/access'),
    summary: () => api.get('/hub/summary'),
    members: () => api.get('/hub/members'),
    // Espaces
    listSpaces: () => api.get('/hub/spaces'),
    // Liste plate filtrée (KPI clic → active | mine | stage_blocked | overdue | closed)
    listAllProjects: (filter = 'active') => api.get('/hub/projects', { params: { filter } }),
    createSpace: (data) => api.post('/hub/spaces', data),
    updateSpace: (id, data) => api.patch(`/hub/spaces/${id}`, data),
    deleteSpace: (id, force = false) => api.delete(`/hub/spaces/${id}${force ? '?force=true' : ''}`),
    // Thèmes
    listThemes: (params) => api.get('/hub/themes', { params }),
    createTheme: (data) => api.post('/hub/themes', data),
    updateTheme: (id, data) => api.patch(`/hub/themes/${id}`, data),
    deleteTheme: (id) => api.delete(`/hub/themes/${id}`),
    // Projets
    listProjects: (spaceId, params = {}) => api.get(`/hub/spaces/${spaceId}/projects`, { params }),
    createProject: (spaceId, data) => api.post(`/hub/spaces/${spaceId}/projects`, data),
    getProject: (id) => api.get(`/hub/projects/${id}`),
    updateProject: (id, data) => api.patch(`/hub/projects/${id}`, data),
    moveStage: (id, stageId) => api.post(`/hub/projects/${id}/move-stage`, { stage_id: stageId }),
    closeProject: (id, status, comment) => api.post(`/hub/projects/${id}/close`, { status, comment }),
    reopenProject: (id) => api.post(`/hub/projects/${id}/reopen`),
    deleteProject: (id) => api.delete(`/hub/projects/${id}`),
    // Admin
    setUserAccess: (userId, access, projectIds = [], spaceIds = []) =>
        api.put(`/hub/admin/users/${userId}/access`, { access, project_ids: projectIds, space_ids: spaceIds }),
    // Tâches (Phase 2a)
    listTasks: (projectId) => api.get(`/hub/projects/${projectId}/tasks`),
    createTask: (projectId, data) => api.post(`/hub/projects/${projectId}/tasks`, data),
    updateTask: (taskId, data) => api.patch(`/hub/tasks/${taskId}`, data),
    deleteTask: (taskId) => api.delete(`/hub/tasks/${taskId}`),
    // Alertes intelligentes (Phase 3)
    myAlerts: () => api.get('/hub/my-alerts'),
    myTasks: (statusFilter) => api.get('/hub/my-tasks', { params: statusFilter ? { status: statusFilter } : {} }),
    savePipelineAsTemplate: (projectId, data) => api.post(`/hub/projects/${projectId}/save-pipeline-as-template`, data),
    myTemplates: (spaceId) => api.get('/hub/my-templates', { params: spaceId ? { space_id: spaceId } : {} }),
    creationLimit: () => api.get('/hub/creation-limit'),
    requestOverride: (reason) => api.post('/hub/request-override', { reason }),
    listOverrideRequests: (status) => api.get('/hub/override-requests', { params: status ? { status } : {} }),
    decideOverride: (reqId, decision, comment, bonusSlots = 1) =>
        api.post(`/hub/override-requests/${reqId}/decide`, { decision, comment, bonus_slots: bonusSlots }),
    adminRunAlerts: () => api.post('/hub/admin/run-alerts'),
    // Mur d'idées & propositions (Phase 2b — général)
    listWall: () => api.get('/hub/wall'),
    wallUnreadCount: () => api.get('/hub/wall/unread-count'),
    markWallSeen: () => api.post('/hub/wall/mark-seen'),
    createPost: (content, postType = 'discussion', mentions = []) => api.post('/hub/wall', { content, post_type: postType, mentions }),
    updatePost: (id, data) => api.patch(`/hub/wall/${id}`, data),
    deletePost: (id) => api.delete(`/hub/wall/${id}`),
    votePost: (id) => api.post(`/hub/wall/${id}/vote`),
    commentPost: (id, content, mentions = []) => api.post(`/hub/wall/${id}/comment`, { content, mentions }),
    deleteComment: (postId, commentId) => api.delete(`/hub/wall/${postId}/comment/${commentId}`),
    toggleWallPostReaction: (postId, emoji) => api.post(`/hub/wall/${postId}/reaction`, { emoji }),
    toggleWallCommentReaction: (postId, commentId, emoji) => api.post(`/hub/wall/${postId}/comment/${commentId}/reaction`, { emoji }),
    convertToProject: (postId, data) => api.post(`/hub/wall/${postId}/convert-to-project`, data),

    // Mur de projet (Phase 2c) — messages + attachments + réactions + commentaires
    listProjectWall: (projectId) => api.get(`/hub/projects/${projectId}/wall`),
    createProjectWallMessage: (projectId, content, mentions = [], config = {}) => {
        const { clientRequestId, ...requestConfig } = config;
        return api.post(`/hub/projects/${projectId}/wall`, { content, mentions, client_request_id: clientRequestId }, requestConfig);
    },
    editProjectWallMessage: (projectId, messageId, content, mentions = []) => api.patch(`/hub/projects/${projectId}/wall/${messageId}`, { content, mentions }),
    markProjectVisited: (projectId) => api.post(`/hub/projects/${projectId}/mark-visited`),
    uploadProjectWallAttachment: (projectId, messageId, file, onUploadProgress, config = {}) => {
        const form = new FormData();
        form.append('file', file);
        const { clientRequestId, ...requestConfig } = config;
        if (clientRequestId) form.append('client_request_id', clientRequestId);
        return api.post(`/hub/projects/${projectId}/wall/${messageId}/attachment`, form, {
            headers: { 'Content-Type': 'multipart/form-data' },
            onUploadProgress,
            // Pas de timeout pour les gros uploads (vidéos)
            timeout: 0,
            ...requestConfig,
        });
    },
    projectWallAttachmentUrl: (projectId, messageId, attachmentId, disposition = 'inline') => {
        const base = api.defaults.baseURL || '';
        return `${base}/hub/projects/${projectId}/wall/${messageId}/attachments/${attachmentId}?disposition=${disposition}`;
    },
    deleteProjectWallMessage: (projectId, messageId) => api.delete(`/hub/projects/${projectId}/wall/${messageId}`),
    toggleProjectWallReaction: (projectId, messageId, emoji) => api.post(`/hub/projects/${projectId}/wall/${messageId}/reaction`, { emoji }),
    commentProjectWallMessage: (projectId, messageId, content, mentions = []) => api.post(`/hub/projects/${projectId}/wall/${messageId}/comment`, { content, mentions }),
    deleteProjectWallComment: (projectId, messageId, commentId) => api.delete(`/hub/projects/${projectId}/wall/${messageId}/comment/${commentId}`),
    toggleProjectWallCommentReaction: (projectId, messageId, commentId, emoji) => api.post(`/hub/projects/${projectId}/wall/${messageId}/comment/${commentId}/reaction`, { emoji }),
    toggleProjectWallPin: (projectId, messageId) => api.post(`/hub/projects/${projectId}/wall/${messageId}/toggle-pin`),

    // Recherche globale de fichiers (Hub)
    searchAttachments: (params = {}) => api.get('/hub/attachments/search', { params }),

    // Fil d'activité (mouvements sur les projets)
    recentActivity: (limit = 20) => api.get('/hub/recent-activity', { params: { limit } }),
    markActivitySeen: () => api.post('/hub/activity/mark-all-seen'),
    unreadCount: () => api.get('/hub/unread-count'),
};

export const projectWallMediaClient = messageId => {
    const path = (projectId, id) => `/hub/projects/${projectId}/wall/${messageId}/attachments/${id}`;
    return {
        metadata: (projectId, id, signal) => api.get(path(projectId, id) + '/meta', { signal, skipRetry: true }),
        thumbnail: (projectId, id, signal) => api.get(path(projectId, id) + '/thumbnail', { signal, skipRetry: true, responseType: 'blob', timeout: 15000 }),
        preview: (projectId, id, signal, timeout = 30000) => api.get(path(projectId, id) + '/preview', { signal, skipRetry: true, responseType: 'blob', timeout }),
        download: (projectId, id, signal) => api.get(path(projectId, id), { params: { disposition: 'attachment' }, signal, skipRetry: true, responseType: 'blob' }),
        retry: (projectId, id) => api.post(path(projectId, id) + '/retry-preview'),
    };
};

